import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { ReactComponent as CheckList } from 'assets/checklist.svg';
import { ReactComponent as DownloadIcon } from 'assets/download.svg';
import reviewProcessing from 'assets/reviewProcessing.png';
import { ReactComponent as Writing } from 'assets/writing.svg';

import { downloadReviewPdf } from 'utlis/pdfUtils';
import { formatDate } from 'utlis/string_format';

const TAB_OPTIONS = {
  REVIEW: 'review',
  CONTEXT: 'context',
};

import { PDF_STATUS } from 'utlis/constants';

import ProductForm from './ProductForm';
import ReviewCompleted from './ReviewCompleted';

const ReviewResults = ({ isReviewComplete, reviewData, title, productDescription }) => {
  const [selectedTab, setSelectedTab] = useState(TAB_OPTIONS.REVIEW);
  const [isDownloading, setIsDownloading] = useState(false);

  const pdfProcess = useMemo(
    () => reviewData?.post_processes?.find(data => data.process_type === 1),
    [reviewData?.post_processes]
  );

  const isPdfReady = useMemo(
    () => pdfProcess && pdfProcess.status === PDF_STATUS.READY && pdfProcess.result_data?.pdf_link,
    [pdfProcess]
  );

  const isPdfFailed = useMemo(
    () => pdfProcess && pdfProcess.status === PDF_STATUS.FAILED,
    [pdfProcess]
  );

  const handleDownloadReport = async () => {
    if (!pdfProcess?.result_data?.pdf_link) {
      return;
    }

    await downloadReviewPdf(reviewData, pdfProcess.result_data.pdf_link, setIsDownloading);
  };

  return (
    <div
      className="flex-[3] max-w-xl 2xl:max-w-[632px] bg-white rounded-2xl shadow-lg flex flex-col mx-8"
    >
      <div
        className="flex flex-col items-start p-7 2xl:p-10 gap-10 w-full bg-white rounded-2xl shadow-lg z-10 flex-1"
        style={{
          boxShadow: '0px 4px 16px -2px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.04)',
        }}
      >
        <div className="flex w-full justify-center gap-24 mb-16 border-b">
          <p
            className={cn(
              'flex flex-1 align-center justify-center cursor-pointer pb-12 gap-6 border-b-2',
              {
                'text-primary-500 border-b-primary-500': selectedTab === TAB_OPTIONS.CONTEXT,
                'border-b-white text-gray-500': selectedTab !== TAB_OPTIONS.CONTEXT,
              }
            )}
            onClick={() => setSelectedTab(TAB_OPTIONS.CONTEXT)}
          >
            <CheckList className="w-4" />
            <span>
              Context
            </span>
          </p>
          <p
            className={cn(
              'flex flex-1 align-center justify-center cursor-pointer pb-12 gap-6 border-b-2',
              {
                'text-primary-500 border-b-2 border-b-primary-500': selectedTab === TAB_OPTIONS.REVIEW,
                'border-b-white text-gray-500': selectedTab !== TAB_OPTIONS.REVIEW,
              }
            )}
            onClick={() => setSelectedTab(TAB_OPTIONS.REVIEW)}
          >
            <Writing className="w-4" />
            <span>
              Review results
            </span>
          </p>
        </div>

        {selectedTab === TAB_OPTIONS.CONTEXT && (
          <div className="w-full">
            <ProductForm title={title} productDescription={productDescription} reviewData={reviewData} mode="display" />
          </div>
        )}
        {selectedTab === TAB_OPTIONS.REVIEW && (
          isReviewComplete
            ? (
              <ReviewCompleted reviewData={reviewData} />
            )
            : (
              <div className="flex flex-col justify-center items-center gap-10 w-full flex-1">
                <img src={reviewProcessing} alt="icon" className="w-36 h-36 2xl:w-48 2xl:h-48" />
                <div className="flex flex-col items-center gap-4">
                  <h2 className="sm:text-xl 2xl:text-2xl font-semibold text-gray-900 text-center">
                    Your review is being processed
                  </h2>
                  <p className="sm:text-md 2xl:text-lg text-gray-900 text-center">
                    We'll notify you when it's ready
                  </p>
                </div>
              </div>
            )
        )}
        <div className="flex items-end w-full mt-auto h-10 gap-10">
          <div className="flex flex-col gap-8 h-10 text-xs">
            <span className="text-gray-500 h-4">
              {isReviewComplete ? 'Completed on' : 'Last updated'}
            </span>
            <span className="text-gray-900 h-4">
              {reviewData?.updated_at ? formatDate(reviewData?.updated_at) : 'Just now'}
            </span>
          </div>

          {isReviewComplete && (
            <>
              {isPdfReady
                ? (
                  <button
                    onClick={handleDownloadReport}
                    disabled={!isPdfReady || isDownloading}
                    title={!isPdfReady ? (isPdfFailed ? 'PDF generation failed' : 'Download PDF') : 'Download PDF'}
                    className={cn(
                      'flex items-center gap-8 px-5 py-2.5 text-xs 2xl:text-sm font-medium rounded-lg',
                      'transition-colors ml-auto',
                      {
                        'bg-primary-500 text-white hover:bg-primary-600 cursor-pointer': isPdfReady && !isDownloading,
                        'bg-gray-100 text-gray-600 cursor-not-allowed': !isPdfReady || isDownloading,
                      }
                    )}
                  >
                    <DownloadIcon alt="Download" />
                    {isDownloading ? 'Downloading...' : 'Download report'}
                  </button>
                )
                : (
                  <p className="ml-auto text-sm text-gray-500">
                    {isPdfFailed ? 'PDF generation failed' : 'PDF is being generated...'}
                  </p>
                )}
            </>
          )}
        </div>
      </div>
    </div>

  );
};

ReviewResults.propTypes = {
  isReviewComplete: PropTypes.bool.isRequired,
  reviewData: PropTypes.object,
  title: PropTypes.string.isRequired,
  productDescription: PropTypes.string.isRequired,
};

export default ReviewResults;
